import React, { FC, useMemo } from 'react'
import styles from '../SearchProvider.module.scss'
import { ChipListFilter } from 'components/SA/Filters'
import { getHrefFunction, updateClientSideUrl } from '../helpers/url'
import { SelectedFacets } from 'types/SearchProvider'
import Skeleton from 'components/SA/Skeleton'
import useAvoidLayoutShift from 'hooks/useAvoidLayoutShift'
import { handleScrollToTop } from '../helpers'
import { updateFacetValues } from '../helpers/facets'
import useReduxSearchProvider from '../hooks/useReduxSearchProvider'
import { CustomHandleUpdateSelectedFacetParams, UseReduxSearchProviderResponse } from '../types'

type FacetListChipsProps = {
  baseUrl: string
  nextAppRoute: string
  onClose?: (facet: SelectedFacets) => void
  filtersWhiteList?: ReadonlyArray<string>
  customClearFilterCallback?: () => void
  minSelectedToBeVisible?: number
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customChipListFilterStyles?: string
}

const CUSTOM_PRICE_FACET_NAME_ARRAY = ['custom__geo_prices', 'custom__print_all_prices']
const PRICE_FACET_NAME_ARRAY = ['geo_prices', 'print_all_prices']

const FacetListChips: FC<FacetListChipsProps> = ({
  onClose,
  filtersWhiteList,
  baseUrl,
  nextAppRoute,
  customClearFilterCallback,
  minSelectedToBeVisible = 1,
  useReduxProviderHook = useReduxSearchProvider,
  customHandleUpdateSelectedFacet,
  customChipListFilterStyles = '',
}) => {
  const { isSkeletonVisible } = useAvoidLayoutShift()
  const { selectedFacets, basicQueryParams, pathParams, sharedFacetNames } = useReduxProviderHook()

  const allSelectedFacets = Object.entries(selectedFacets).reduce(
    (acc, [facetName, attributes]) => {
      if (
        ['has_prints', 'hitsPerPage', 'page'].includes(facetName) ||
        (facetName === 'category' && attributes?.[0] === 'all')
      ) {
        return acc
      }

      if (Array.isArray(attributes)) {
        attributes.forEach((attributeName) => {
          acc.push({
            facetName,
            attributeName,
            title: String(attributeName),
          })
        })
      }
      return acc
    },
    [] as {
      facetName: string
      attributeName: string
      title: string
    }[]
  )

  const onClickSelectedFacet = ({ facetName, attributeName }: SelectedFacets) => {
    handleScrollToTop(() => {
      const newSelectedFacets = updateFacetValues({
        selectedFacets,
        facetName,
        attributeValue: attributeName,
      })
      const isSortRemoved = facetName === 'sort'

      const isCustomPrice = CUSTOM_PRICE_FACET_NAME_ARRAY.includes(facetName) // specific rule for custom price filter
      const originalFacetName = isCustomPrice ? facetName.split('custom__')?.[1] : facetName // specific rule for custom price filter

      const newSelectedFacetsAndQueryParams = {
        ...newSelectedFacets,
        ...basicQueryParams,
        ...(isCustomPrice ? { [originalFacetName]: [] } : {}), // specific rule for custom price filter
        page: [''],
      }

      if (isSortRemoved) {
        delete newSelectedFacetsAndQueryParams['sort']
      }

      const href = getHrefFunction({
        facetsAndQueryParams: newSelectedFacetsAndQueryParams,
        pathParams,
        sharedFacetNames,
      })()

      const newUrl = `${baseUrl}${href}`

      if (Boolean(customHandleUpdateSelectedFacet)) {
        customHandleUpdateSelectedFacet?.({
          newSelectedFacets: newSelectedFacetsAndQueryParams,
          newUrl,
          facetName,
          facetValue: attributeName,
        })
        return
      }

      updateClientSideUrl(newUrl, nextAppRoute)
    })
  }

  const handleOnRemove = (facet) => {
    onClickSelectedFacet(facet)
    onClose?.(facet)
  }

  const handleClearFilters = () => {
    handleScrollToTop(() => {
      if (!!customClearFilterCallback) {
        customClearFilterCallback()
        return
      }

      if (Boolean(customHandleUpdateSelectedFacet)) {
        customHandleUpdateSelectedFacet?.({
          newSelectedFacets: {},
          newUrl: baseUrl,
        })
        return
      }

      updateClientSideUrl(baseUrl, nextAppRoute)
    })
  }

  const facetsList = useMemo(
    () =>
      allSelectedFacets.filter(({ facetName }) => {
        const hasCustomPrice = allSelectedFacets.some(({ facetName }) =>
          CUSTOM_PRICE_FACET_NAME_ARRAY.includes(facetName)
        )

        if (hasCustomPrice && PRICE_FACET_NAME_ARRAY.includes(facetName)) {
          return false
        }

        return !!filtersWhiteList ? filtersWhiteList.includes(facetName) : true
      }),
    [allSelectedFacets, filtersWhiteList]
  )

  const isCategoryPresent = facetsList.some(({ facetName }) => facetName === 'category')
  const quantityOfFilterToBeVisible = isCategoryPresent
    ? minSelectedToBeVisible + 1
    : minSelectedToBeVisible

  if (facetsList.length < quantityOfFilterToBeVisible) {
    return null
  }

  return (
    <div className={styles.facetListChipsContainer}>
      {isSkeletonVisible ? (
        Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index.toString()} width='75px' height='40px' />
        ))
      ) : (
        <ChipListFilter
          facetsList={facetsList}
          onClose={handleOnRemove}
          onClearFilter={handleClearFilters}
          isClearFilterVisible={true}
          customChipListFilterStyles={customChipListFilterStyles}
        />
      )}
    </div>
  )
}

export default FacetListChips
